import { API, APIState, Difficulty, PlayedTile } from "./api";

export type Player = string;

export interface AI {
    proportion: number;
    randomness: number;
}

export interface PartyInfo {
    ais: AI[];
    players: Player[];
}

export type RoomEvent =
    | {
          type: "PlayerJoined" | "PlayerLeft";
          player: Player;
      }
    | {
          type: "AIJoined";
          difficulty: Difficulty;
      }
    | {
          type: "AILeft";
          index: number;
      };

export type GameEvent = {
    type: "TurnAction";
    state: APIState;
    committed: boolean;
};

export type ServerToClientMessage =
    | {
          type: "RoomChange";
          event: RoomEvent;
          info: PartyInfo;
      }
    | {
          type: "GameEvent";
          event: GameEvent;
      }
    | {
          type: "GameError";
          error: any;
      }
    | {
          type: "Invalid";
          reason: string;
      }
    | {
          type: "Disconnection";
          reason: string;
      };

type GameMove =
    | {
          type: "Pass";
      }
    | {
          type: "Exchange";
          tiles: Array<boolean>;
      }
    | {
          type: "Play";
          played_tiles: Array<PlayedTile>;
          commit_move: boolean;
      }
    | {
          type: "AddToDictionary";
          word: string;
      };

export type ClientToServerMessage =
    | {
          type: "Load" | "StartGame";
      }
    | {
          type: "GameMove";
          move: GameMove;
      }
    | {
          type: "AddAI";
          difficulty: Difficulty;
      }
    | {
          type: "RemoveAI";
          index: number;
      };

interface PromiseInput {
    resolve: (value: GameEvent) => void;
    reject: (error: any) => void;
    type: string; // recorded for debug purposes
}

export class WSAPI implements API {
    private socket: WebSocket;
    private currentPromiseInput: PromiseInput | null;

    constructor(socket: WebSocket) {
        this.socket = socket;
        this.currentPromiseInput = null;
        this.socket.addEventListener("message", (event) => {
            let data: ServerToClientMessage = JSON.parse(event.data);
            console.log("Message from server ", event.data);
            if (data.type == "GameEvent") {
                if (this.currentPromiseInput != null) {
                    this.currentPromiseInput.resolve(data.event);
                    this.currentPromiseInput = null;
                } else {
                    console.error(
                        "Received game data but no promise is queued",
                    );
                    console.error({ data });
                }
            } else if (data.type == "GameError") {
                if (this.currentPromiseInput != null) {
                    this.currentPromiseInput.reject(data.error);
                    this.currentPromiseInput = null;
                } else {
                    console.error(
                        "Received error game data but no promise is queued",
                    );
                    console.error({ data });
                }
            }
        });
    }

    private register_promise(
        resolve: (value: GameEvent) => void,
        reject: (value: any) => void,
        type: string,
    ) {
        const newPromise = {
            resolve: resolve,
            reject: reject,
            type: type,
        };

        if (this.currentPromiseInput != null) {
            console.error(
                "We are setting a new promise before the current one has resolved",
            );
            console.error("Current promise: ", this.currentPromiseInput);
            console.error("New promise: ", newPromise);

            // Some of the rejects take statements from the server and log them; maybe don't send this to reject
            //this.currentPromiseInput.reject("New promise was registered");
        }
        this.currentPromiseInput = newPromise;
    }

    add_to_dictionary(word: string): Promise<APIState> {
        return new Promise((resolve, reject) => {
            this.register_promise(resolve, reject, "AddToDictionary");
            let event: ClientToServerMessage = {
                type: "GameMove",
                move: {
                    type: "AddToDictionary",
                    word,
                },
            };
            this.socket.send(JSON.stringify(event));
        }).then((game_event: GameEvent) => {
            return game_event.state;
        });
    }

    exchange(selection: Array<boolean>): Promise<APIState> {
        return new Promise((resolve, reject) => {
            this.register_promise(resolve, reject, "Exchange");
            let event: ClientToServerMessage = {
                type: "GameMove",
                move: {
                    type: "Exchange",
                    tiles: selection,
                },
            };
            this.socket.send(JSON.stringify(event));
        }).then((game_event: GameEvent) => {
            return game_event.state;
        });
    }

    load(wait: boolean): Promise<APIState> {
        return new Promise((resolve, reject) => {
            this.register_promise(resolve, reject, `Load with wait=${wait}`);
            if (!wait) {
                let event: ClientToServerMessage = {
                    type: "Load",
                };
                this.socket.send(JSON.stringify(event));
            }
        }).then((game_event: GameEvent) => {
            return game_event.state;
        });
    }

    pass(): Promise<APIState> {
        return new Promise((resolve, reject) => {
            this.register_promise(resolve, reject, "Pass");
            let event: ClientToServerMessage = {
                type: "GameMove",
                move: {
                    type: "Pass",
                },
            };
            this.socket.send(JSON.stringify(event));
        }).then((game_event: GameEvent) => {
            return game_event.state;
        });
    }

    play(tiles: Array<PlayedTile>, commit: boolean): Promise<APIState> {
        return new Promise((resolve, reject) => {
            this.register_promise(resolve, reject, "Play");
            let event: ClientToServerMessage = {
                type: "GameMove",
                move: {
                    type: "Play",
                    played_tiles: tiles,
                    commit_move: commit,
                },
            };
            this.socket.send(JSON.stringify(event));
        }).then((game_event: GameEvent) => {
            return game_event.state;
        });
    }
}
